import React, { useContext, useEffect, useState } from 'react';

import { Skeleton } from '@mui/material';
import { useTranslation, Trans } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useIntercom } from 'react-use-intercom';

import SlackLogo from '../../../../../assets/icons/slack-logo.svg?url';
import { SRP_AUTH_FLOW_NO_OAUTH } from '../../../../../config/amplify';
import CardNE from '../../../../../shared/components/CardNE/CardNE';
import CardStatusNE from '../../../../../shared/components/CardStatusNE/CardStatusNE';
import StyledButtonNE from '../../../../../shared/components/StyledButtonNE/StyledButtonNE';
import { INTEGRATIONS_ROUTE } from '../../../../../shared/constants/auth';
import { CLIENTS_PORTAL_URL, SLACK_OAUTH_URL } from '../../../../../shared/constants/constants';
import { SLACK_INTEGRATION_ERROR_MODAL, SLACK_INTEGRATION_SUCCESS_MODAL } from '../../../../../shared/constants/modal';
import { CompanyContext } from '../../../../../shared/context/company/companyContext';
import { ModalContext } from '../../../../../shared/context/modal/modalContext';
import useQueryParams from '../../../../../shared/hooks/useQueryParams/useQueryParams';
import { IModalContent } from '../../../../../shared/interfaces/modal';
import Company from '../../../../../shared/services/api/companies/companies';
import { TRANSLATIONS_SETTINGS } from '../../../../../shared/translations/settings';
import { MODAL_TYPES } from '../../../../../shared/types/errorHandlingTypes';
import { redirectToPage } from '../../../../../shared/utils/utils';

import './SlackCard.scss';

interface SettingsOrganizationParams {
  code?: string;
}

const SlackCard = () => {
  const [connectionStatus, setConnectionStatus] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingCompany, setLoadingCompany] = useState<boolean>(false);

  const { company, slackIntegration, isLoadingCompanyData, changeSlackIntegrationState } = useContext(CompanyContext);
  const { handleOpenModal, handleCloseModal } = useContext(ModalContext);

  const { code } = useQueryParams<SettingsOrganizationParams>();
  const { show } = useIntercom();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const integrateSlack = async (code: string) => {
    try {
      setLoading(true);
      await Company.integrateSlack(company.id, code);
      handleOpenModal(modalContent['success']);
      setConnectionStatus(true);
      changeSlackIntegrationState(true);
    } catch (error) {
      handleOpenModal(modalContent['error']);
      setConnectionStatus(false);
    } finally {
      setLoading(false);
    }
  };

  const redirectToSlack = () => {
    SRP_AUTH_FLOW_NO_OAUTH();
    const redirectUri = `${CLIENTS_PORTAL_URL}${INTEGRATIONS_ROUTE}`;
    redirectToPage(navigate, SLACK_OAUTH_URL + redirectUri, '_self');
  };

  const showSupportChat = () => {
    show();
  };

  const getModalContent = (type: MODAL_TYPES) => {
    if (type === 'success') {
      if (SLACK_INTEGRATION_SUCCESS_MODAL.primaryButton) {
        SLACK_INTEGRATION_SUCCESS_MODAL.primaryButton.action = handleCloseModal;
      }
      return SLACK_INTEGRATION_SUCCESS_MODAL;
    } else if (type === 'error') {
      if (SLACK_INTEGRATION_ERROR_MODAL.primaryButton) {
        SLACK_INTEGRATION_ERROR_MODAL.primaryButton.action = redirectToSlack;
      }
      return SLACK_INTEGRATION_ERROR_MODAL;
    }
  };

  useEffect(() => {
    setLoadingCompany(isLoadingCompanyData);
  }, [isLoadingCompanyData]);

  const modalContent: {
    success: IModalContent;
    error: IModalContent;
  } = {
    success: getModalContent('success') || SLACK_INTEGRATION_SUCCESS_MODAL,
    error: getModalContent('error') || SLACK_INTEGRATION_ERROR_MODAL
  };

  useEffect(() => {
    setConnectionStatus(!!slackIntegration);
  }, [slackIntegration]);

  useEffect(() => {
    if (code && connectionStatus === false && slackIntegration === false) {
      integrateSlack(code);
    }
  }, [code, slackIntegration, connectionStatus]);

  return (
    <>
      {loading || loadingCompany ? (
        <Skeleton className="integration-card-skeleton" variant="rectangular" />
      ) : (
        <div className="slack-card" data-experience="new">
          <CardNE>
            <header>
              <h3 className="title">{t(TRANSLATIONS_SETTINGS.integrations.slack.title)}</h3>
              <img className="accordeon-card-image" src={SlackLogo} width={36} height={36} alt="Integration Slack" />
            </header>

            {!connectionStatus ? (
              <>
                <div>
                  <p className="description">{t(TRANSLATIONS_SETTINGS.integrations.slack.descriptionDisconnected)}</p>
                </div>
                <div>
                  <StyledButtonNE buttonType="secondary" action={redirectToSlack}>
                    {t(TRANSLATIONS_SETTINGS.integrations.slack.buttonDisconnected)}
                  </StyledButtonNE>
                </div>
              </>
            ) : (
              <>
                <CardStatusNE
                  type="success"
                  message={t(TRANSLATIONS_SETTINGS.integrations.slack.statusCardConnected)}
                />
                <div>
                  <p className="description">
                    <Trans
                      i18nKey={TRANSLATIONS_SETTINGS.integrations.slack.descriptionConnected}
                      components={{
                        supportLink: <a className="text-link" onClick={showSupportChat} aria-label="support" />
                      }}
                    />
                  </p>
                </div>
                {/* <div>
                  <StyledButtonNE buttonType='secondary' disabled={true}>
                    {t(TRANSLATIONS_SETTINGS.integrations.slack.buttonConnected)}
                  </StyledButtonNE>
                </div> */}
              </>
            )}
          </CardNE>
        </div>
      )}
    </>
  );
};

export default SlackCard;
